import { Camera, Circle, Loader2, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { TeamLiveStrap } from './TeamLiveStrap';
import type { TeamLiveTileDrag } from './TeamLiveSortableGrid';
import type { TeamLiveDeviceEntry } from '@/types';
import { formatUserName } from '@/utils/userName';

export interface DevicePresenceTileProps {
  entry: TeamLiveDeviceEntry;
  /** Drag affordance from the sortable grid; null when reordering is off. */
  drag?: TeamLiveTileDrag | null;
  /** Cam-on authority for the viewer (wall remote cam control). */
  canControl?: boolean;
  /** Server-side pending declaration (fresh + un-acked). */
  pending?: boolean;
  /** Local request in flight. */
  isRequesting?: boolean;
  /** Director cam-on action (stream only). */
  onRequestOn?: () => void;
  /**
   * Director record action (2026-10-04 wall cam record model): stream AND
   * record from idle — the same publication declaration with record: true.
   */
  onRecord?: () => void;
}

/**
 * An online-but-idle team device on the Team Live multiview (device-grid
 * tiles): the phone runs Trackcast and reports a fresh heartbeat, but it is
 * not publishing, so there is no player and no director controls — presence
 * only, plus the director's cam-on control. Deliberately mirrors
 * TeamLiveTile's placeholder + strap anatomy so the wall still reads as one
 * system; the sky accent (vs. a stream's green) says "standby, not live".
 */
export function DevicePresenceTile({
  entry,
  drag,
  canControl,
  pending,
  isRequesting,
  onRequestOn,
  onRecord,
}: DevicePresenceTileProps) {
  const { t } = useTranslation('pages');
  const memberName = formatUserName(entry.member);
  const requested = pending || isRequesting;

  // Record (stream + record) — mirrors the live tile's record pill (same red
  // outline = same meaning), firing the same declaration with record: true.
  const recordControl =
    canControl && onRecord ? (
      <button
        type="button"
        disabled={requested}
        onClick={onRecord}
        // Must not start a strap drag.
        onPointerDown={(event) => event.stopPropagation()}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50',
          'border border-red-500/60 bg-black/50 text-red-300 backdrop-blur-sm hover:bg-black/70',
        )}
      >
        {requested ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <Circle className="h-2.5 w-2.5 fill-current" />
        )}
        {requested ? t('teamLive.camRequested') : t('teamLive.record')}
      </button>
    ) : null;

  const camControl =
    canControl && onRequestOn ? (
      <button
        type="button"
        disabled={requested}
        onClick={onRequestOn}
        // Must not start a strap drag.
        onPointerDown={(event) => event.stopPropagation()}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50',
          requested
            ? 'border border-sky-400/60 bg-black/50 text-sky-300 backdrop-blur-sm'
            : 'bg-sky-500 text-white hover:bg-sky-400',
        )}
      >
        {requested ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <Camera className="h-2.5 w-2.5" />
        )}
        {requested ? t('teamLive.camRequested') : t('teamLive.camOn')}
      </button>
    ) : null;

  const strap = (
    <TeamLiveStrap
      title={memberName}
      subtitle={entry.device.name ?? t('teamLive.deviceTile.untitledDevice')}
      statusLabel={t('teamLive.deviceTile.status')}
      accentClassName="bg-sky-400"
      drag={drag}
    >
      {recordControl}
      {camControl}
    </TeamLiveStrap>
  );

  return (
    <div
      data-testid="device-presence-tile"
      className="relative min-h-0 overflow-hidden rounded-lg border border-white/10 bg-secondary-900"
    >
      <div className="flex h-full flex-col items-center justify-center gap-2 text-white/50">
        <Smartphone className="h-6 w-6" />
        <span className="text-xs">{t('teamLive.deviceTile.status')}</span>
      </div>

      {/*
        No player means no bottom bar to host the strap, so the tile renders
        it directly — same placement as TeamLiveTile's placeholder path.
      */}
      <div
        {...drag?.strapProps}
        className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/75 via-black/45 to-transparent px-2 py-1.5"
      >
        {strap}
      </div>
    </div>
  );
}
